import React, { useState } from 'react';
import { authAPI } from '../services/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from './ui/card';
import { Loader2, Lock, User, AlertCircle, ArrowLeft } from 'lucide-react';
import { Alert, AlertDescription } from './ui/alert';
import ForgotPasswordModal from './ForgotPasswordModal';

const LoginPage = ({ onLoginSuccess, onBack }) => {
    const [loading, setLoading] = useState(false);
    const [showForgotPasswordModal, setShowForgotPasswordModal] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const logo = require('../lib/logo.png');

    const heroBg = require('../lib/hero_page.png');

    // Login State
    const [loginData, setLoginData] = useState({ username: '', password: '' });

    const handleLogin = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');
        try {
            const response = await authAPI.login(loginData);
            localStorage.setItem('user', JSON.stringify(response.data));
            onLoginSuccess(response.data);
        } catch (err) {
            setError(err.response?.data?.message || 'Invalid credentials. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen w-full flex items-center justify-center relative py-12 px-4 overflow-y-auto font-sans text-white">
            {/* Uniform Background Image with Overlay */}
            <div className="fixed inset-0 z-0">
                <img
                    src={heroBg}
                    alt="Background"
                    className="absolute inset-0 w-full h-full object-cover brightness-125"
                />
                <div className="absolute inset-0 bg-[#001524]/60 mix-blend-multiply" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#001524] via-transparent to-[#001524]/40" />
            </div>

            {/* Background decorative elements */}
            <div className="fixed top-[-10%] left-[-10%] w-[40%] h-[40%] bg-sky-500/20 rounded-full blur-[120px] pointer-events-none z-0" />
            <div className="fixed bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-orange-500/10 rounded-full blur-[120px] pointer-events-none z-0" />

            {/* Back Button - Top Left Corner - Responsive */}
            {onBack && (
                <button
                    onClick={onBack}
                    className="fixed top-3 left-3 sm:top-4 sm:left-4 z-20 group flex items-center gap-2 sm:gap-3 px-3 sm:px-5 py-2 sm:py-2.5 rounded-full bg-white/5 border border-white/10 hover:bg-white/10 hover:border-white/20 hover:shadow-[0_0_20px_rgba(255,255,255,0.1)] transition-all duration-300 backdrop-blur-md"
                    title="Back to Home"
                >
                    <div className="bg-white/10 p-1.5 rounded-full group-hover:bg-[#FF7D00] group-hover:scale-110 transition-all duration-300">
                        <ArrowLeft className="h-4 w-4 text-white" />
                    </div>
                    <span className="hidden sm:inline text-sm font-semibold tracking-wide text-white/90 group-hover:text-white transition-colors">Back to Home</span>
                </button>
            )}

            <div className="z-10 w-full max-w-[440px] animate-in fade-in zoom-in-95 duration-700">
                <div className="flex justify-center mb-10">
                    <div className="relative group">
                        <div className="absolute inset-0 bg-[#FF7D00] rounded-3xl blur-2xl opacity-20 group-hover:opacity-40 transition-opacity"></div>
                        <div className="relative bg-white/10 p-5 rounded-[2rem] backdrop-blur-2xl border border-white/20 shadow-2xl transition-transform hover:scale-110 duration-500">
                            <img src={logo} alt="JIT Logo" className="w-20 h-20 object-contain animate-spin-slow" />
                        </div>
                    </div>
                </div>

                <Card className="border-white/10 bg-[#001524]/40 backdrop-blur-3xl shadow-[0_32px_64px_-16px_rgba(0,0,0,0.6)] rounded-[2.5rem] overflow-hidden">
                    <div className="absolute top-0 left-0 w-full h-1.5 bg-gradient-to-r from-transparent via-[#FF7D00] to-transparent opacity-50"></div>
                    
                    <CardHeader className="space-y-2 text-center pt-10 pb-6">
                        <CardTitle className="text-4xl font-black tracking-tight text-white select-none">
                            Welcome <span className="text-[#FF7D00]">Back</span>
                        </CardTitle>
                        <CardDescription className="text-slate-400 font-medium tracking-wide">
                            Access your mission control dashboard
                        </CardDescription>
                    </CardHeader>
                    
                    <CardContent className="px-8 pb-10">
                        {error && (
                            <div className="mb-6 p-4 rounded-2xl bg-rose-500/10 border border-rose-500/20 text-rose-400 flex items-center gap-3 animate-in shake-1 duration-500">
                                <AlertCircle className="h-5 w-5 shrink-0" />
                                <p className="text-xs font-bold">{error}</p>
                            </div>
                        )}

                        {success && (
                            <div className="mb-6 p-4 rounded-2xl bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 flex items-center gap-3 animate-in zoom-in-95">
                                <AlertCircle className="h-5 w-5 shrink-0" />
                                <p className="text-xs font-bold">{success}</p>
                            </div>
                        )}

                        <form onSubmit={handleLogin} className="space-y-6">
                            <div className="space-y-2">
                                <Label className="text-slate-300 text-[10px] font-black uppercase tracking-[0.2em] ml-1">Identity</Label>
                                <div className="relative group">
                                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                                        <User className="h-5 w-5 text-slate-500 group-focus-within:text-[#FF7D00] transition-colors" />
                                    </div>
                                    <Input
                                        placeholder="Username / Email"
                                        value={loginData.username}
                                        onChange={(e) => setLoginData({ ...loginData, username: e.target.value })}
                                        className="h-14 pl-12 bg-white/5 border-white/10 text-white rounded-2xl focus:ring-4 focus:ring-[#FF7D00]/10 focus:border-[#FF7D00]/40 transition-all font-medium placeholder:text-slate-600"
                                        required
                                    />
                                </div>
                            </div>

                            <div className="space-y-2">
                                <div className="flex justify-between items-center ml-1">
                                    <Label className="text-slate-300 text-[10px] font-black uppercase tracking-[0.2em]">Secret Key</Label>
                                    <button
                                        type="button"
                                        onClick={() => setShowForgotPasswordModal(true)}
                                        className="text-[10px] font-black uppercase tracking-widest text-slate-500 hover:text-[#FF7D00] transition-colors"
                                    >
                                        Recovery
                                    </button>
                                </div>
                                <div className="relative group">
                                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                                        <Lock className="h-5 w-5 text-slate-500 group-focus-within:text-[#FF7D00] transition-colors" />
                                    </div>
                                    <Input
                                        type="password"
                                        placeholder="••••••••"
                                        value={loginData.password}
                                        onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                                        className="h-14 pl-12 bg-white/5 border-white/10 text-white rounded-2xl focus:ring-4 focus:ring-[#FF7D00]/10 focus:border-[#FF7D00]/40 transition-all font-medium placeholder:text-slate-600"
                                        required
                                    />
                                </div>
                            </div>
                            
                            <Button 
                                type="submit" 
                                className="w-full h-14 bg-[#FF7D00] hover:bg-[#E66000] text-white rounded-2xl font-black uppercase tracking-[0.15em] shadow-xl shadow-[#FF7D00]/20 hover:shadow-[#FF7D00]/40 transition-all active:scale-[0.98] mt-4" 
                                disabled={loading}
                            >
                                {loading ? (
                                    <div className="flex items-center gap-3">
                                        <Loader2 className="animate-spin h-5 w-5" />
                                        <span>Authenticating...</span>
                                    </div>
                                ) : (
                                    <div className="flex items-center gap-2">
                                        <span>Initialize Session</span>
                                        <ArrowLeft className="h-4 w-4 rotate-180" />
                                    </div>
                                )}
                            </Button>
                        </form>
                    </CardContent>
                    
                    <CardFooter className="flex flex-col gap-4 bg-white/5 py-6 border-t border-white/5">
                        <p className="text-[10px] text-center font-black uppercase tracking-widest text-slate-500 select-none">
                            © {new Date().getFullYear()} JIT Secure Infrastructure
                        </p>
                    </CardFooter>
                </Card>
            </div>

            {showForgotPasswordModal && (
                <ForgotPasswordModal
                    onClose={() => setShowForgotPasswordModal(false)}
                    onSuccess={() => {
                        setSuccess("Password reset successful. Please login with new password.");
                    }}
                />
            )}
        </div>
    );
};

export default LoginPage;
